const parseDateToTimestamp = (value: string): number | null => {
  const normalized = value.trim();
  if (!normalized) return null;

  const match = normalized.match(
    /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );

  if (match) {
    const [, dayRaw, monthRaw, yearRaw, hourRaw, minuteRaw, secondRaw] = match;
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    const hour = Number(hourRaw ?? 0);
    const minute = Number(minuteRaw ?? 0);
    const second = Number(secondRaw ?? 0);

    const date = new Date(year, month - 1, day, hour, minute, second, 0);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getHours() !== hour ||
      date.getMinutes() !== minute ||
      date.getSeconds() !== second
    ) {
      return null;
    }

    return date.getTime();
  }

  const native = Date.parse(normalized);
  return Number.isFinite(native) ? native : null;
};


const DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const TIME_REGEX = /^(\d{2}):(\d{2})$/;

type DateParts = { year: number; month: number; day: number };
type TimeParts = { hour: number; minute: number };

const parseDateParts = (value: string): DateParts | null => {
  const normalized = (value ?? '').trim();
  if (!normalized) return null;

  const match = normalized.match(DATE_REGEX);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
};

const parseTimeParts = (value: string): TimeParts | null => {
  const normalized = (value ?? '').trim();
  if (!normalized) return null;

  const match = normalized.match(TIME_REGEX);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (hour > 23 || minute > 59 ) return null;

  return { hour, minute };
};

export const isValidDateInput = (value: string): boolean => {
  const normalized = (value ?? '').trim();
  if (!normalized) return true;
  return parseDateParts(normalized) !== null;
};

export const isValidTimeInput = (value: string): boolean => {
  const normalized = (value ?? '').trim();
  if (!normalized) return true;
  return parseTimeParts(normalized) !== null;
};

const combineBound = (
  dateRaw: string,
  timeRaw: string,
  defaultTime: TimeParts
): number | null => {
  const date = parseDateParts(dateRaw);
  if (!date) return null;

  const time = parseTimeParts(timeRaw) ?? defaultTime;
  return new Date(
    date.year,
    date.month - 1,
    date.day,
    time.hour,
    time.minute,
    0
  ).getTime();
};

const DAY_START: TimeParts = { hour: 0, minute: 0 };
const DAY_END: TimeParts = { hour: 23, minute: 59 };

export type DateTimeRangeInput = {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
};

export const buildDateTimeRangePredicate = ({
  fromDate,
  fromTime,
  toDate,
  toTime,
}: DateTimeRangeInput) => {
  let lower = combineBound(fromDate, fromTime, DAY_START);
  let upper = combineBound(toDate, toTime, DAY_END);

  if (lower == null && upper == null) return null;

  if (lower != null && upper != null && lower > upper) {
    [lower, upper] = [upper, lower];
  }

  return (value: string | null | undefined) => {
    if (!value) return false;
    const ts = parseDateToTimestamp(value);
    if (ts == null) return false;
    if (lower != null && ts < lower) return false;
    if (upper != null && ts > upper) return false;
    return true;
  };
};

export const getDateFromCompareArg = (arg: unknown): string | null => {
  const candidate = (arg as { data?: unknown })?.data ?? arg;
  return typeof candidate === 'string' ? candidate : null;
};

export const makeSortDateCompleted =
  (getDate: (arg: unknown) => string | null) =>
  (order: 'asc' | 'desc') =>
  (a: unknown, b: unknown): number => {
    const aDate = getDate(a);
    const bDate = getDate(b);

    const aTs = aDate == null ? null : parseDateToTimestamp(aDate);
    const bTs = bDate == null ? null : parseDateToTimestamp(bDate);

    if (aTs == null && bTs == null) return 0;
    if (aTs == null) return 1;
    if (bTs == null) return -1;

    return order === 'asc' ? aTs - bTs : bTs - aTs;
  };
